// The `#hashtag` syntax of todo titles and descriptions, including tags with spaces. The same grammar as
// `code/sync_todo_control/hashtags.py` (read it for the full description); both are tested against
// `__tests__/hashtag-cases.json`, so they cannot drift apart.
//
//   #work   #"Tag with Spaces" (`\"` and `\\` escaped)   #[She said "Hi"] (no escapes, no `]`)
//
// The quotes of `#"…"` can be any of `QUOTE_CHARS` (the `"` and the look-alikes an OS substitutes while typing, like `“` or
// `„`): any of them opens, any of them closes and `\` escapes any of them.
//
// A user allows these spellings (`TagStyle`) as an ordered list: `formatHashtag` writes a name in the first allowed one that can
// hold it, and a name none can hold percent-encoded, `#Tag%20with%20Spaces` (`%` itself is `%25`). A spelling the user did not allow
// is no hashtag when reading (it stays text), except the percent-encoded one, which is always read.
//
// A tag needs a boundary on both sides: the start/end of the text, whitespace, `,` or `;`, or, between two tags, nothing
// at all (`#a#b`). Whatever does not parse (empty or over-long names, control characters, a quote or bracket that is never
// closed) is not a tag and stays text. In lenient mode, meant for the edges of a title or description, the closing quote of
// `#"…"` is the first unescaped quote followed by a boundary, so `#"She said "Hi""` is the tag `She said "Hi"`.

export const MAX_TAG_NAME_LENGTH = 100
export const QUOTE_CHARS = '"«»“”„‟‹›❛❜❝❞❮❯〝〞〟＂'

export type TagStyle = 'plain' | 'brackets' | 'quotes'
export const TAG_STYLES: readonly TagStyle[] = ['plain', 'brackets', 'quotes']
export const DEFAULT_TAG_STYLES: readonly TagStyle[] = TAG_STYLES

// The spellings the signed-in user allows (`User.tag_styles`), in their order; what the functions below use when no `styles` is
// passed. Set once the settings are loaded (`setTagStyles`), all of them until then.
let activeStyles: readonly TagStyle[] = DEFAULT_TAG_STYLES

export function setTagStyles(styles: readonly TagStyle[] | null | undefined): void {
  activeStyles = styles && styles.length ? [...styles] : DEFAULT_TAG_STYLES
}

export function getTagStyles(): readonly TagStyle[] {
  return activeStyles
}

export interface HashtagToken {
  start: number // (index of the `#`)
  end: number // (index after the closing quote or bracket, or the last letter)
  name: string
}

const WORD = /^(?:[\p{L}\p{N}_]|%[0-9A-Fa-f]{2})+/u
const PERCENT = /%[0-9A-Fa-f]{2}/
const WORD_NAME = /^[\p{L}\p{N}_]+$/u
const CONTROL = /\p{Cc}/u

export function isQuote(char: string | undefined): boolean {
  return char !== undefined && char !== '' && QUOTE_CHARS.includes(char)
}

function isQuoteOrBackslash(char: string): boolean {
  return char === '\\' || isQuote(char)
}

function isSpace(char: string): boolean {
  return /\s/u.test(char)
}

function isBoundaryAfter(text: string, index: number): boolean {
  return index >= text.length || isSpace(text[index]) || ',;#'.includes(text[index])
}

function isBoundaryBefore(text: string, index: number, previousEnd: number | null): boolean {
  return (
    index === 0 ||
    isSpace(text[index - 1]) ||
    ',;'.includes(text[index - 1]) ||
    previousEnd === index
  )
}

// A name that can be a tag: not empty, no surrounding whitespace, at most 100 characters, no control characters.
export function isValidTagName(name: string, maxLength: number = MAX_TAG_NAME_LENGTH): boolean {
  return (
    name.length > 0 && name === name.trim() && [...name].length <= maxLength && !CONTROL.test(name)
  )
}

function parseQuoted(text: string, start: number, lenient: boolean): [string, number] | null {
  let name = ''
  let index = start + 2
  while (index < text.length) {
    const char = text[index]
    if (char === '\r' || char === '\n') return null // a name never spans lines
    if (char === '\\' && index + 1 < text.length && isQuoteOrBackslash(text[index + 1])) {
      name += text[index + 1]
      index += 2
      continue
    }
    if (QUOTE_CHARS.includes(char)) {
      if (isBoundaryAfter(text, index + 1)) return [name, index + 1]
      if (!lenient) return null
    }
    name += char
    index += 1
  }
  return null
}

function parseBracketed(text: string, start: number): [string, number] | null {
  const close = text.indexOf(']', start + 2)
  if (close < 0 || !isBoundaryAfter(text, close + 1)) return null
  return [text.slice(start + 2, close), close + 1]
}

function parseAt(
  text: string,
  start: number,
  lenient: boolean,
  maxLength: number,
  styles: readonly TagStyle[],
): HashtagToken | null {
  if (text[start] !== '#') return null
  const following = text[start + 1]
  let parsed: [string, number] | null
  if (isQuote(following)) parsed = styles.includes('quotes') ? parseQuoted(text, start, lenient) : null
  else if (following === '[') parsed = styles.includes('brackets') ? parseBracketed(text, start) : null
  else {
    const word = WORD.exec(text.slice(start + 1))
    parsed =
      word && isBoundaryAfter(text, start + 1 + word[0].length)
        ? [word[0], start + 1 + word[0].length]
        : null
    // the percent-encoded spelling is always read, a plain word only when the user allows it
    if (parsed && !PERCENT.test(parsed[0]) && !styles.includes('plain')) parsed = null
  }
  if (!parsed) return null
  let name = parsed[0]
  if (isQuote(following) || following === '[') name = name.trim()
  else {
    try {
      name = decodeURIComponent(name)
    } catch {
      return null // (not valid UTF-8)
    }
  }
  return isValidTagName(name, maxLength) ? { start, end: parsed[1], name } : null
}

// Every hashtag in `text`, in order. `lenient` is the mal-escaped quote rule, meant for the edges only.
export function hashtagTokens(
  text: string,
  options: { lenient?: boolean; styles?: readonly TagStyle[] } = {},
): HashtagToken[] {
  return scan(text, options.lenient ?? false, MAX_TAG_NAME_LENGTH, options.styles ?? activeStyles)
}

// The tags that would be valid if they were not longer than 100 characters: they are left as text, and the UI warns.
export function tooLongHashtags(text: string): HashtagToken[] {
  return scan(text, false, Infinity, activeStyles).filter((tag) => [...tag.name].length > MAX_TAG_NAME_LENGTH)
}

function scan(
  text: string,
  lenient: boolean,
  maxLength: number,
  styles: readonly TagStyle[],
): HashtagToken[] {
  const found: HashtagToken[] = []
  let previousEnd: number | null = null
  let index = 0
  while (index < text.length) {
    if (text[index] === '#' && isBoundaryBefore(text, index, previousEnd)) {
      const tag = parseAt(text, index, lenient, maxLength, styles)
      if (tag) {
        found.push(tag)
        previousEnd = tag.end
        index = tag.end
        continue
      }
    }
    index += 1
  }
  return found
}

// Whether `style` can spell `name` (quotes can spell any name).
export function canHold(style: TagStyle, name: string): boolean {
  if (style === 'plain') return WORD_NAME.test(name)
  if (style === 'brackets') return !name.includes('[') && !name.includes(']')
  return true
}

// Whether `name` is a valid tag name that one of the allowed `styles` can spell (the check of an input; no percent-encoding).
export function canWrite(name: string, styles: readonly TagStyle[] = activeStyles): boolean {
  return isValidTagName(name) && styles.some((style) => canHold(style, name))
}

// `DTGB Bot` -> `DTGB%20Bot`: word characters stay, every other UTF-8 byte (and `%`) becomes `%XX`.
export function percentEncode(name: string): string {
  const encoder = new TextEncoder()
  return [...name]
    .map((char) =>
      /^[\p{L}\p{N}_]$/u.test(char)
        ? char
        : [...encoder.encode(char)].map((byte) => `%${byte.toString(16).toUpperCase().padStart(2, '0')}`).join(''),
    )
    .join('')
}

// The name in the first of the allowed `styles` that can hold it (`#name`, `#[name]`, or `#"name"` with `\` and every quote
// escaped), else percent-encoded (`#a%20b`). The result parses back to the same name.
export function formatHashtag(name: string, styles: readonly TagStyle[] = activeStyles): string {
  for (const style of styles) {
    if (!canHold(style, name)) continue
    if (style === 'plain') return `#${name}`
    if (style === 'brackets') return `#[${name}]`
    const escaped = [...name].map((char) => (isQuoteOrBackslash(char) ? `\\${char}` : char)).join('')
    return `#"${escaped}"`
  }
  return `#${percentEncode(name)}`
}

// Whether a summary says nothing besides `tag` (or nothing at all): what a box prefilled with the tag of a tag page holds
// until something is typed, and then there is no todo to add.
export function isTagOnly(
  summary: string,
  tag: string,
  styles: readonly TagStyle[] = activeStyles,
): boolean {
  const text = summary.trim().toLowerCase()
  return text === '' || text === formatHashtag(tag, styles).toLowerCase()
}
